import React, { useState } from 'react';
import { Button } from '@common/Button';
import { Input } from '@common/FormFields';
import { Plus, Minus, Route } from 'lucide-react';
import { logError, logInfo } from '@utils/logger'; 

export const ItineraryRouteBuilder = ({ onRouteCalculated, onCancel }) => {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [waypoints, setWaypoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addWaypoint = () => {
    setWaypoints([...waypoints, '']);
  };

  const removeWaypoint = (index) => {
    const newWaypoints = [...waypoints];
    newWaypoints.splice(index, 1);
    setWaypoints(newWaypoints);
  };

  const updateWaypoint = (index, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index] = value;
    setWaypoints(newWaypoints);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate inputs
    setLoading(true);

    if (!origin.trim()) {
      setError('L\'origine est requise');
      setLoading(false);
      return;
    }

    if (!destination.trim()) {
      setError('La destination est requise');
      setLoading(false);
      return;
    }

    try {
      // Filter out empty waypoints
      const validWaypoints = waypoints.filter(wp => wp.trim() !== '');
      
      // Log the request details
      logInfo(`Calculating route from "${origin}" to "${destination}" with ${validWaypoints.length} waypoints`);
      if (validWaypoints.length > 0) {
        logInfo(`Waypoints: ${validWaypoints.join(', ')}`);
      }
      
      // Call the parent component's route calculation function
      await onRouteCalculated(origin, validWaypoints, destination);
      
      // Close the route builder if successful
      // Don't close automatically so user can see the results
      // onCancel();
    } catch (error) {
      logError('Failed to calculate route:', error);
      setError(error.message || 'Erreur lors du calcul de l\'itinéraire');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800/50 p-4 rounded-lg space-y-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Origin */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">
            Origine
          </label>
          <Input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="Adresse complète (ex: 1000 Rue Sherbrooke, Montréal, QC)"
            required
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>

        {/* Waypoints */}
        {waypoints.map((waypoint, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={waypoint}
              onChange={(e) => updateWaypoint(index, e.target.value)}
              placeholder={`Point intermédiaire ${index + 1} (adresse complète)`}
              className="bg-gray-700 border-gray-600 text-white flex-1"
            />
            <Button
              type="button"
              onClick={() => removeWaypoint(index)}
              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded"
            >
              <Minus className="w-4 h-4" />
            </Button>
          </div>
        ))}

        {/* Add Waypoint Button */}
        <Button
          type="button"
          onClick={addWaypoint}
          className="w-full bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Ajouter un point intermédiaire
        </Button>

        {/* Destination */}
        <div>
          <label className="block text-sm font-medium text-white mb-1">
            Destination
          </label>
          <Input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="Adresse complète (ex: 1000 Rue Sherbrooke, Montréal, QC)"
            required
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-400 text-sm bg-red-500/10 p-3 rounded">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
            loading={loading}
          >
            <Route className="w-4 h-4" />
            Calculer
          </Button>
        </div>
      </form>
    </div>
  );
};